import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import useAxiosPrivate from '../../api/hooks/useAxiosPrivate'
import {
  SupportListFilters,
  SupportRequest,
} from '../../utils/types/supportTypes'
import { TTableSettings } from '../../utils/types/types'
import { supportFiltersInit } from '../../utils/initialisations/supportInit'
import { supportFilterStrategy } from '../../utils/helpers/supportHelper'

export const SUPPORT_REQUESTS_LIST_QUERY_KEY = ['supportRequestsList']

const useFetchSupportRequests = () => {
  const axiosPrivate = useAxiosPrivate()

  const [supportFilters, setSupportFilters] =
    useState<SupportListFilters>(supportFiltersInit)
  const [isAutoRefresh, setIsAutoRefresh] = useState<boolean>(true)

  const [tableSettings, setTableSettings] = useState<TTableSettings>({
    itemsPerPage: 10,
    page: 0,
    totalItems: 0,
    refreshData: false,
  })

  const { data, isPending, error, refetch } = useQuery({
    queryKey: [
      ...SUPPORT_REQUESTS_LIST_QUERY_KEY,
      tableSettings.page,
      tableSettings.itemsPerPage,
      supportFilters,
    ],
    queryFn: async () => {
      const queryPagination = new URLSearchParams({
        page: String(tableSettings.page + 1),
        itemsPerPage: String(tableSettings.itemsPerPage),
      })
      const parsedFilters = supportFilterStrategy(supportFilters)

      const response = await axiosPrivate.get(
        `/api/support-requests?${queryPagination}${parsedFilters}`
      )

      return {
        supportRequests: response.data['hydra:member'] as SupportRequest[],
        totalItems: response.data['hydra:totalItems'] as number,
      }
    },
    retry: 0,
    refetchInterval: isAutoRefresh ? 60_000 : false,
  })

  const handleChangeSupportFilters = (newFilters: SupportListFilters) => {
    setSupportFilters(newFilters)
    setTableSettings((prev) => ({ ...prev, page: 0 }))
  }

  return {
    supportRequests: data?.supportRequests ?? [],
    totalItems: data?.totalItems ?? 0,
    isPending,
    error,
    refetch,
    tableSettings,
    setTableSettings,
    supportFilters,
    handleChangeSupportFilters,
    isAutoRefresh,
    setIsAutoRefresh,
  } as const
}

export default useFetchSupportRequests
